// External Packages
import { useState, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  MdAdd,
  MdAddTask,
  MdChecklist,
  MdRefresh,
  MdSyncProblem,
} from "react-icons/md";

// Internal Packages
import { UtilityContext } from "../../context/util-context";
import SearchBar from "../UI/SearchBar";
import PaymentCard from "./PaymentCard";
import Heading from "../UI/Heading";
import FilterMenu from "../UI/FilterMenu";
import PaginationMenu from "../UI/PaginationMenu";
import DateFilter from "../UI/DateFilter";
import { requestAdminAPI } from "../../api/posh-api";
import LoadingCard from "../UI/LoadingCard";
import ListLabels from "../UI/ListLabels";
import NoRecordsFound from "../UI/NoRecordsFound";
import InvoiceModal from "./InvoiceModal";
import ExportCsvButton from "../UI/ExportCsvButton";
import DomainSwitch from "../UI/DomainSwitch";
import { formatCurrency } from "../../util/format-text";

/*
  * All Payments Page
  This page displays all payments in the database.
*/

export default function PaymentsPage() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filter
  const defaultFilters = {
    query: "",
    limit: 100,
    scope: "All",
    type: "All",
    status: "",
    paid: "",
    category: "",
    date: "dateCreated",
    sort: "dateCreated",
    sd: "",
    ed: "",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  // Initialize States
  const [payments, setPayments] = useState([]);
  const [filter, setFilter] = useState(paramFilters);
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(false);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);

      // Send Request
      const data = await requestAdminAPI("search", {
        table: "payment",
        limit: filter.limit,
        filters: filter,
      });

      // Replace URL
      let url = "/payments?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      setPayments(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== FILTER OPTIONS ===================
  const filterOptions = [
    {
      label: "Date",
      options: [
        { label: "Date Created", value: "dateCreated" },
        { label: "Date Due", value: "dueDate" },
        { label: "Date Paid", value: "datePaid" },
      ],
      selected: filter.date,
    },
    {
      label: "Type",
      options: ["All", "Payment", "Invoice"],
      selected: filter.type,
    },
    {
      label: "Category",
      options: [
        "Booking",
        "Subscription",
        "Deposit",
        "Damage",
        "Insurance",
        "Tolls",
        "Ticket",
        "Miles",
        "Charging",
        "Violation",
        "Other",
      ],
      selected: filter.category,
      type: "multiple",
    },
    {
      label: "Status",
      options: ["Succeeded", "Pending", "Refunded", "Delinquent"],
      selected: filter.status,
      type: "multiple",
    },
    {
      label: "Paid",
      options: [

        { label: "Cash", value: "_cash" },
        { label: "Clover", value: "_clover" },
        { label: "Zelle", value: "_zelle" },
        { label: "Stripe", value: "_stripe" },
      ],
      selected: filter.paid,
      type: "multiple",
    },
  ];

  // Calculate Total and Average
  let total = 0;
  payments.forEach((payment) => {
    total += Number(payment.amount);
  });
  let average = payments.length ? total / payments.length : 0;

  // Apply Filters
  useEffect(() => {
    fetchRecords();
  }, [filter]);

  return (
    <div className="container grid">
      <InvoiceModal open={invoice} setOpen={setInvoice} />
      <Heading title="Payments" isBackArrow={true}>
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <FilterMenu
          onSubmit={(f) => setFilter({ ...filter, ...f })}
          options={filterOptions}
        />
        <DomainSwitch
          on={filter.scope !== "All"}
          onSwitch={(scope) => setFilter({ ...filter, scope })}
        />
        <MdSyncProblem
          className="icon"
          onClick={(e) => navigate("/disputes", e)}
        />
        <MdAddTask
          onClick={() => navigate("/payments/report")}
          className="icon"
          title="Report"
        />
        <MdChecklist
          onClick={() => navigate("/transactions")}
          className="icon"
          title="Billing"
        />
        <MdAdd
          onClick={() => setInvoice(true)}
          className="text-3xl icon"
          title="Add Payment"
        />
        <MdRefresh
          onClick={fetchRecords}
          className="text-2xl icon"
          title="Refresh"
        />
        <ExportCsvButton
          array={payments}
          filename="payments"
          mapping={{
            key: "category",
            column: "mappedCategory",
            map: (v) => {
              if (v === "booking" || v === "miles" || v === "violation")
                return "Subscription Booking Fees";
              else if (v === "subscription")
                return "Subscription Plan Set-Up Fees";
              else if (v === "other" || v === "tolls" || v === "ticket")
                return "Claims, Outfits, Other Fees";
              else return v;
            },
          }}
        />
      </Heading>

      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />

      {loading ? (
        <LoadingCard columns={5} max={6} />
      ) : payments.length > 0 ? (
        <div className="list">
          <div className="labels lg:grid-cols-8">
            <ListLabels
              labels={[
                { key: "dateCreated", name: "Date Created" },
                "Customer",
                { name: "Description", classes: "lg:col-span-3" },
                { key: "dueDate", name: "Due Date" },
                { key: "datePaid", name: "Date Paid" },
                { key: "amount", name: "Amount" },
              ]}
              selected={filter.sort}
              setSelected={(sort) => setFilter({ ...filter, sort })}
            />
          </div>
          {payments.map((payment) => (
            <PaymentCard key={payment.id} payment={payment} />
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        limit={filter.limit}
        setLimit={(l) => setFilter({ ...filter, limit: l })}
        loading={loading}
        count={payments.length}
      >
        <p className="text-stone-400">
          Total:{" "}
          <span className="font-semibold text-stone-200">
            {formatCurrency(total)}
          </span>
        </p>
        <p className="text-stone-400">
          Average:{" "}
          <span className="font-semibold text-stone-200">
            {formatCurrency(average)}
          </span>
        </p>
      </PaginationMenu>
    </div>
  );
}
